/* Swiper-like slider drawn with CSS only.

   It is what SwiperComponent renders on the server and until it mounts, so with JavaScript
   off (or before hydration) a slider still looks like its Swiper: the same slidesPerView,
   spaceBetween, breakpoints and grid rows, a row you can scroll sideways. It takes the very
   props SwiperComponent receives, so there is no second configuration to keep in sync.

   Server-safe on purpose: no hooks and no "use client". */

// stable short id of the layout, so equal configs share one stylesheet class
const hash_config = (value) => {
  const text = JSON.stringify(value);
  let hash = 5381;
  for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
  return (hash >>> 0).toString(36);
};

const is_number = (value) => typeof value === "number" && Number.isFinite(value);

const columns_rule = (slides_per_view, gap) =>
  is_number(slides_per_view)
    ? `calc((100% - (${slides_per_view} - 1) * ${gap}px) / ${slides_per_view})`
    : "max-content"; // "auto": slides keep their own width

const build_css = (id, { slidesPerView, spaceBetween, breakpoints, grid }) => {
  const base_gap = is_number(spaceBetween) ? spaceBetween : 0;
  const base_per_view = slidesPerView ?? 1;
  const base_rows = is_number(grid?.rows) ? grid.rows : 1;

  const sel = `.ss-${id}`;
  let css =
    `${sel}{--ss-gap:${base_gap}px;--ss-rows:${base_rows};--ss-cols:${columns_rule(base_per_view, base_gap)};` +
    `display:grid;grid-auto-flow:column;grid-template-rows:repeat(var(--ss-rows),minmax(0,auto));` +
    `grid-auto-columns:var(--ss-cols);gap:var(--ss-gap);overflow-x:auto;overscroll-behavior-x:contain;` +
    `scroll-snap-type:x proximity;scrollbar-width:none}` +
    `${sel}::-webkit-scrollbar{display:none}` +
    `${sel}>*{min-width:0;scroll-snap-align:start}`;

  // Swiper applies a breakpoint from its width up, and later ones override earlier ones
  let gap = base_gap;
  let per_view = base_per_view;
  const widths = Object.keys(breakpoints || {})
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

  for (const width of widths) {
    const bp = breakpoints[width] || {};
    if (is_number(bp.spaceBetween)) gap = bp.spaceBetween;
    if (bp.slidesPerView !== undefined) per_view = bp.slidesPerView;

    const rules = [`--ss-gap:${gap}px`, `--ss-cols:${columns_rule(per_view, gap)}`];
    if (is_number(bp.grid?.rows)) rules.push(`--ss-rows:${bp.grid.rows}`);

    css += `@media (min-width:${width}px){${sel}{${rules.join(";")}}}`;
  }

  return css;
};

export default function StaticSlider({
  slides = [],
  slidesPerView,
  spaceBetween,
  breakpoints,
  grid,
  className = "",
  dir,
  pagination = false,
  hidePaginationOnDesktop = false,
}) {
  const id = hash_config({ slidesPerView, spaceBetween, breakpoints, grid });

  return (
    <div className="relative swiper-main-wrapper">
      <style>{build_css(id, { slidesPerView, spaceBetween, breakpoints, grid })}</style>
      <div className={`ss-${id} ${className}`} dir={dir}>
        {slides.map((slide, index) => (
          <div key={`static-slide-${index}`}>{slide}</div>
        ))}
      </div>
      {/* جای خالی صفحه‌شمار: در `.slider-category-pagination` صفحه‌شمار Swiper داخل جریان است (۱۲px فاصله + ۷px
          دایره‌ها) و بعد از mount اضافه می‌شد، پس هرچه زیر اسلایدر بود ۱۹px پایین می‌پرید (CLS). در بقیه‌ی
          اسلایدرها صفحه‌شمار absolute است و این‌جا ارتفاعی ندارد (global.css). */}
      {pagination && (
        <div className={`static-slider-pagination ${hidePaginationOnDesktop ? "lg:hidden" : ""}`} aria-hidden="true" />
      )}
    </div>
  );
}
